/**
 * Drum Pads Component - 4x4 Grid
 */
import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Drum } from 'lucide-react';
import { DRUM_KIT, PAD_LAYOUT } from './MIDIEngine';

export const DrumPads = ({
  onPadHit,
  activePads = [],
  isRecording = false,
}) => {
  const [pressedPads, setPressedPads] = useState(new Set());

  const handlePadStart = useCallback((drumName, e) => {
    e.preventDefault();
    
    // Calculate velocity from touch/click pressure or position
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY || e.touches?.[0]?.clientY || rect.top;
    const relY = (y - rect.top) / rect.height;
    const velocity = Math.round(80 + relY * 47); // 80-127
    
    setPressedPads(prev => new Set([...prev, drumName]));
    
    const drum = DRUM_KIT[drumName];
    if (drum) {
      onPadHit?.(drumName, drum.note, velocity);
    }
    
    // Visual feedback - auto release after short delay
    setTimeout(() => {
      setPressedPads(prev => {
        const next = new Set(prev);
        next.delete(drumName);
        return next;
      });
    }, 100);
  }, [onPadHit]);

  return (
    <div className="bg-zinc-900/95 backdrop-blur-xl border border-zinc-800 rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Drum className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium">Drum Pads</span>
          {isRecording && (
            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full animate-pulse">
              REC
            </span>
          )}
        </div>
      </div>
      
      {/* 4x4 Pad Grid */}
      <div className="grid grid-cols-4 gap-2" data-testid="drum-pads">
        {PAD_LAYOUT.flat().map((drumName) => {
          const drum = DRUM_KIT[drumName];
          if (!drum) return null;
          
          const isActive = activePads.includes(drumName) || pressedPads.has(drumName);
          
          return (
            <motion.button
              key={drumName}
              className={`
                relative aspect-square rounded-lg cursor-pointer select-none
                flex flex-col items-center justify-center gap-1
                transition-all duration-75
                ${isActive 
                  ? `bg-gradient-to-br ${drum.color} shadow-lg scale-95` 
                  : 'bg-zinc-800 hover:bg-zinc-700'
                }
                ${isRecording ? 'ring-1 ring-red-500/30' : ''}
              `}
              whileTap={{ scale: 0.9 }}
              onMouseDown={(e) => handlePadStart(drumName, e)}
              onTouchStart={(e) => handlePadStart(drumName, e)}
              data-testid={`drum-pad-${drumName}`}
            >
              <span className={`text-xs font-medium ${isActive ? 'text-white' : 'text-zinc-300'}`}>
                {drum.name}
              </span>
              <span className={`text-[10px] ${isActive ? 'text-white/70' : 'text-zinc-500'}`}>
                {drum.note}
              </span>
            </motion.button>
          );
        })}
      </div>
      
      {/* Pad Info */}
      <div className="mt-3 text-xs text-zinc-500">
        Tap pads to play • Velocity from tap position
      </div>
    </div>
  );
};

export default DrumPads;
